import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpenCheck,
  Scale,
  FileSpreadsheet,
  TrendingUp,
  Banknote,
  BookOpen,
  BookMarked,
  ScrollText,
  RefreshCw,
  Lock,
  Sparkles,
  AlertTriangle,
  ChevronRight,
  RotateCcw,
  Download,
  Eye,
  CheckCircle2,
  FileText,
} from 'lucide-react';
import { erpApi, type ErpCheck, type ErpSnapshot, type FinancialYearRecord } from '../api';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, formatRangeLabel } from '../lib/dateFilter';
import { formatLocalYmd } from '../lib/loanScheduleDates';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { exportFyReportExcel, exportFyReportPdf } from '../lib/erpFyReport';
import { useTranslation } from '../lib/i18n';
import { getCurrentUser, isAuditorUser, useCanWrite } from '../lib/permissions';
import { toast } from 'react-toastify';

const inr = (n: number) => {
  const v = Math.round(Number(n) || 0);
  return `${v < 0 ? '−' : ''}₹${Math.abs(v).toLocaleString('en-IN')}`;
};

const OPENING_FIELDS: { key: string; label: string }[] = [
  { key: 'cash', label: 'Cash in hand' },
  { key: 'bank', label: 'Bank balance' },
  { key: 'outstanding', label: 'Loans receivable' },
  { key: 'inventory', label: 'Vehicle inventory' },
  { key: 'employeeAdvances', label: 'Employee advances' },
  { key: 'loansTaken', label: 'Loans taken' },
  { key: 'creditors', label: 'Creditors' },
  { key: 'capital', label: 'Capital' },
];

function CheckList({ checks }: { checks: ErpCheck[] }) {
  if (!checks.length) return null;
  const tone: Record<ErpCheck['status'], string> = {
    ok: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-100',
    info: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800/50 dark:bg-sky-950/30 dark:text-sky-100',
    warning: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100',
    error: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-100',
  };
  return (
    <ul className="space-y-1.5">
      {checks.map((c) => (
        <li key={c.code} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${tone[c.status]}`}>
          {c.status === 'ok' ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <span>{c.message}</span>
        </li>
      ))}
    </ul>
  );
}

type ErpTab =
  | 'overview'
  | 'fy'
  | 'trial'
  | 'balance'
  | 'pl'
  | 'cashflow'
  | 'daybook'
  | 'ledger'
  | 'journal'
  | 'reconcile'
  | 'auditreport';

const TABS: { id: ErpTab; labelKey: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', labelKey: 'erp.tab.overview', icon: Sparkles },
  { id: 'fy', labelKey: 'erp.tab.fy', icon: Lock },
  { id: 'trial', labelKey: 'erp.tab.trial', icon: Scale },
  { id: 'balance', labelKey: 'erp.tab.balance', icon: FileSpreadsheet },
  { id: 'pl', labelKey: 'erp.tab.pl', icon: TrendingUp },
  { id: 'cashflow', labelKey: 'erp.tab.cashflow', icon: Banknote },
  { id: 'daybook', labelKey: 'erp.tab.daybook', icon: BookOpen },
  { id: 'ledger', labelKey: 'erp.tab.ledger', icon: BookMarked },
  { id: 'journal', labelKey: 'erp.tab.journal', icon: ScrollText },
  { id: 'reconcile', labelKey: 'erp.tab.reconcile', icon: RefreshCw },
  { id: 'auditreport', labelKey: 'erp.tab.auditreport', icon: Download },
];

function ReportCard({
  title,
  subtitle,
  children,
  exportRows,
  exportTitle,
  exportFilename,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  exportRows?: ExportRow[];
  exportTitle?: string;
  exportFilename?: string;
}) {
  return (
    <div className="app-card min-w-0 overflow-hidden">
      <div className="flex flex-col gap-2 border-b border-zinc-200 bg-zinc-50/80 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/90 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
          {subtitle ? <p className="text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</p> : null}
        </div>
        {exportRows && exportRows.length > 0 && exportTitle && exportFilename ? (
          <ExportMenu title={exportTitle} excelFilename={exportFilename} rows={exportRows} />
        ) : null}
      </div>
      <div className="p-3 sm:p-4">{children}</div>
    </div>
  );
}

function MobileTable({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
  if (!rows.length) return <p className="text-sm text-zinc-500">No data for this period.</p>;
  return (
    <>
      <div className="space-y-2 lg:hidden">
        {rows.map((row, i) => (
          <div key={i} className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
            {headers.map((h, j) => (
              <div key={h} className="flex justify-between gap-2 py-0.5 text-sm">
                <span className="text-zinc-500 dark:text-zinc-400">{h}</span>
                <span className="font-medium text-zinc-900 dark:text-zinc-100">{row[j]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="app-table-wrap hidden overflow-x-auto lg:block">
        <table className="app-table min-w-full">
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} className={j > 0 ? 'tabular-nums' : ''}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ErpSuite({ setActiveItem }: { setActiveItem: (item: string) => void }) {
  const { t } = useTranslation();
  const dr = useDateRange('financial_year');
  const [tab, setTab] = useState<ErpTab>('overview');
  const [snap, setSnap] = useState<ErpSnapshot | null>(null);
  const [years, setYears] = useState<FinancialYearRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLedger, setSelectedLedger] = useState<string>('all');
  const isAuditor = isAuditorUser();
  const canWrite = useCanWrite('erp');
  const isOwner = !!getCurrentUser()?.isOrgOwner;

  const fromIso = dr.range.start ? formatLocalYmd(dr.range.start) : undefined;
  const toIso = dr.range.end ? formatLocalYmd(dr.range.end) : undefined;
  const rangeLabel = formatRangeLabel(dr.range);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [snapshot, fyRes] = await Promise.all([
        erpApi.snapshot({ from: fromIso, to: toIso, useCurrentFy: true }),
        erpApi.listFinancialYears(),
      ]);
      setSnap(snapshot);
      setYears(fyRes.years);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ERP data');
    } finally {
      setLoading(false);
    }
  }, [fromIso, toIso]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCloseFy = async (id: string) => {
    if (!window.confirm(t('erp.confirmCloseFy'))) return;
    try {
      await erpApi.closeFinancialYear(id);
      toast.success(t('erp.fyClosed'));
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not close financial year');
    }
  };

  const latestClosedId = useMemo(() => {
    const closed = years.filter((y) => y.status === 'closed');
    closed.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    return closed[0]?.id || '';
  }, [years]);

  const handleReopenFy = async (fy: FinancialYearRecord) => {
    const reason = window.prompt(
      `Reopen ${fy.label}? Entries dated in this year can be changed again until you close it once more.\n\nReason (kept in the audit log):`
    );
    if (reason === null) return;
    if (reason.trim().length < 5) {
      toast.error('Give a reason of at least 5 characters.');
      return;
    }
    try {
      await erpApi.reopenFinancialYear(fy.id, reason.trim());
      toast.success(`${fy.label} reopened`);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not reopen financial year');
    }
  };

  const handleSaveOpening = async (fy: FinancialYearRecord, balances: Record<string, number>) => {
    try {
      await erpApi.updateFinancialYear(fy.id, { openingBalances: balances });
      toast.success(t('common.saved'));
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save opening balances');
    }
  };

  const handleReverse = async (clientId: string) => {
    const reason = window.prompt(t('erp.reverseReason'));
    if (reason === null) return;
    try {
      await erpApi.reverseJournal(clientId, reason);
      toast.success(t('erp.reversed'));
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not reverse entry');
    }
  };

  const [fyExporting, setFyExporting] = useState(false);
  const downloadFyPdf = async () => {
    if (!snap) return;
    setFyExporting(true);
    try {
      await exportFyReportPdf(snap);
    } finally {
      setFyExporting(false);
    }
  };

  const auditReportRows: ExportRow[] = useMemo(() => {
    if (!snap) return [];
    const rows: ExportRow[] = [
      { Section: 'Financial Health', Particulars: 'Business Score', Value: `${snap.financialHealth.businessScore}/100` },
      { Section: 'Financial Health', Particulars: 'On-time %', Value: `${snap.financialHealth.collectionPct}%` },
      { Section: 'Financial Health', Particulars: 'Profit Margin', Value: `${snap.financialHealth.profitMargin}%` },
      { Section: 'P&L', Particulars: 'Net Profit', Value: snap.profitAndLoss.netProfit },
      { Section: 'Balance Sheet', Particulars: 'Total Assets', Value: snap.balanceSheet.totalAssets },
      { Section: 'Balance Sheet', Particulars: 'Net Worth', Value: snap.balanceSheet.netWorth },
      { Section: 'Cash Flow', Particulars: 'Closing Cash', Value: snap.cashFlow.closingCash },
      { Section: 'Cash Flow', Particulars: 'Closing Bank', Value: snap.cashFlow.closingBank ?? 0 },
    ];
    (snap.checks || []).forEach((c) => {
      rows.push({ Section: 'Checks', Particulars: c.message, Value: c.status.toUpperCase() });
    });
    snap.trialBalance.rows.forEach((r) => {
      rows.push({ Section: 'Trial Balance', Particulars: r.name, Value: `${inr(r.balance)} ${r.side}` });
    });
    return rows;
  }, [snap]);

  const booksStart = snap?.booksStart || null;
  const canEditOpening = (fy: FinancialYearRecord) =>
    fy.status === 'open' && canWrite && (!booksStart || formatLocalYmd(new Date(fy.startDate)) <= booksStart);

  const filteredLedgers = useMemo(() => {
    if (!snap) return [];
    if (selectedLedger === 'all') return snap.ledgerBooks;
    return snap.ledgerBooks.filter((l) => l.code === selectedLedger);
  }, [snap, selectedLedger]);

  if (loading && !snap) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-zinc-500">{t('common.loading')}</div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="app-card overflow-hidden">
        <div className="bg-gradient-to-br from-indigo-950 via-zinc-900 to-emerald-950 px-4 py-3 sm:px-5 sm:py-3.5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <BookOpenCheck className="h-5 w-5 shrink-0 text-emerald-400" />
                <div>
                  <h1 className="text-base font-bold text-white sm:text-lg">{t('erp.title')}</h1>
                  <p className="text-[11px] text-emerald-200/80 sm:text-xs">{t('erp.subtitle')}</p>
                </div>
              </div>
              {isAuditor ? (
                <span className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-0.5 text-[11px] text-emerald-100">
                  <Eye className="h-3 w-3" /> {t('audit.auditorMode')}
                </span>
              ) : null}
            </div>
            <button type="button" onClick={load} className="app-btn-secondary shrink-0 py-1.5 text-xs sm:text-sm">
              <RefreshCw className={`mr-1.5 inline h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              {t('audit.refresh')}
            </button>
          </div>
        </div>
        <div className="border-t border-amber-200/80 bg-amber-50/80 px-4 py-1.5 text-[11px] leading-snug text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/35 dark:text-amber-100 sm:px-5 sm:text-xs">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
          {snap?.disclaimer || t('audit.disclaimer')}
        </div>
      </div>

      {error ? <div className="app-card p-4 text-sm text-rose-600">{error}</div> : null}

      {/* Date filter */}
      <div className="app-card p-3 sm:p-4">
        <DateRangeFilter
          start={dr.range.start}
          end={dr.range.end}
          preset={dr.preset}
          onStart={dr.setStart}
          onEnd={dr.setEnd}
          onPreset={dr.setPreset}
          onClear={dr.clear}
          className="!w-full"
        />
        <p className="mt-2 text-xs text-zinc-500">{rangeLabel}</p>
      </div>

      {/* Tab nav — horizontal scroll row */}
      <nav className="app-tab-bar" aria-label={t('erp.title')}>
        <div className="app-tab-scroll" role="tablist">
          {TABS.map((item) => {
            const Icon = item.icon;
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(item.id)}
                aria-current={active ? 'page' : undefined}
                className={`app-tab-btn ${active ? 'app-tab-btn-active' : 'app-tab-btn-inactive'}`}
              >
                <Icon className="app-tab-icon" aria-hidden />
                <span className="truncate">{t(item.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {snap && tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 col-span-2 lg:col-span-1 dark:border-emerald-800/50 dark:bg-emerald-950/30">
              <p className="text-[11px] font-semibold uppercase text-emerald-800 dark:text-emerald-300">{t('erp.businessScore')}</p>
              <p className="mt-1 text-3xl font-bold text-emerald-900 dark:text-emerald-200">{snap.financialHealth.businessScore}/100</p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
              <p className="text-[11px] font-semibold uppercase text-zinc-500 dark:text-zinc-400">{t('erp.profitMargin')}</p>
              <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-100">{snap.financialHealth.profitMargin}%</p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
              <p className="text-[11px] font-semibold uppercase text-zinc-500 dark:text-zinc-400">{t('erp.collectionPct')}</p>
              <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-100">{snap.financialHealth.collectionPct}%</p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
              <p className="text-[11px] font-semibold uppercase text-zinc-500 dark:text-zinc-400">{t('erp.outstandingPct')}</p>
              <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-100">{snap.financialHealth.outstandingPct}%</p>
            </div>
          </div>

          <ReportCard title={t('erp.aiAuditor')} subtitle={t('erp.aiSubtitle')}>
            <div className="space-y-2">
              {snap.aiAuditor.insights.map((ins, i) => (
                <div
                  key={i}
                  className={`rounded-lg border px-3 py-2 text-sm ${
                    ins.type === 'alert'
                      ? 'border-rose-200 bg-rose-50 text-rose-900'
                      : ins.type === 'warning'
                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                        : 'border-sky-200 bg-sky-50 text-sky-900'
                  }`}
                >
                  {ins.message}
                </div>
              ))}
              {snap.aiAuditor.insights.length === 0 ? (
                <p className="text-sm text-zinc-500">{t('erp.noInsights')}</p>
              ) : null}
            </div>
            {snap.aiAuditor.recommendations.length > 0 ? (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold uppercase text-zinc-500">{t('erp.recommendations')}</p>
                <ul className="space-y-1">
                  {snap.aiAuditor.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </ReportCard>
        </div>
      )}

      {snap && tab === 'fy' && (
        <ReportCard title={t('erp.tab.fy')} subtitle={t('erp.fySubtitle')}>
          <div className="space-y-3">
            {years.map((fy) => (
              <div key={fy.id} className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-700 dark:bg-zinc-900/50">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-zinc-900 dark:text-zinc-100">{fy.label}</p>
                    <p className="text-xs text-zinc-500">
                      {new Date(fy.startDate).toLocaleDateString('en-IN')} –{' '}
                      {new Date(fy.endDate).toLocaleDateString('en-IN')}
                    </p>
                    <span
                      className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        fy.status === 'closed'
                          ? 'bg-zinc-100 text-zinc-600'
                          : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20'
                      }`}
                    >
                      {fy.status === 'closed' ? t('erp.fyClosedStatus') : t('erp.fyOpenStatus')}
                    </span>
                  </div>
                  {fy.status === 'open' && canWrite ? (
                    <button type="button" onClick={() => handleCloseFy(fy.id)} className="app-btn-primary text-sm">
                      <Lock className="mr-1 inline h-4 w-4" />
                      {t('erp.closeFy')}
                    </button>
                  ) : null}
                  {fy.status === 'closed' && isOwner && fy.id === latestClosedId ? (
                    <button type="button" onClick={() => handleReopenFy(fy)} className="app-btn-secondary text-sm">
                      <RotateCcw className="mr-1 inline h-4 w-4" />
                      Reopen year
                    </button>
                  ) : null}
                </div>
                {(() => {
                  const isBooksStart = !!booksStart && formatLocalYmd(new Date(fy.startDate)) === booksStart;
                  const broughtForward = !!booksStart && formatLocalYmd(new Date(fy.startDate)) > booksStart;
                  const editable = canEditOpening(fy);
                  return (
                    <>
                      <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
                        {broughtForward
                          ? 'Opening balances — brought forward automatically from the previous year’s books.'
                          : isBooksStart
                            ? 'Opening balances — your balances on the day you started using Edge Finances (include migrated loans in Loans receivable).'
                            : 'Opening balances — enter your balances on the day you started using Edge Finances.'}
                      </p>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                        {OPENING_FIELDS.map(({ key, label }) => (
                          <div key={key} className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/90">
                            <p className="text-zinc-500 dark:text-zinc-400">{label}</p>
                            {editable ? (
                              <input
                                type="number"
                                aria-label={`${fy.label} opening ${label}`}
                                defaultValue={num(fy.openingBalances?.[key])}
                                onBlur={(e) => {
                                  if (num(e.target.value) === num(fy.openingBalances?.[key])) return;
                                  const next = { ...fy.openingBalances, [key]: num(e.target.value) };
                                  handleSaveOpening(fy, next);
                                }}
                                className="mt-0.5 w-full rounded border border-zinc-200 bg-white px-1.5 py-1 text-sm font-semibold tabular-nums dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
                              />
                            ) : (
                              <p className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{inr(num(fy.openingBalances?.[key]))}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
            {snap.openingBalances ? (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 text-sm dark:border-indigo-800/50 dark:bg-indigo-950/30">
                <p className="font-semibold text-indigo-950 dark:text-indigo-100">Balances at the start of the selected period ({rangeLabel})</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                  {[
                    ['Cash in hand', snap.openingBalances.cash],
                    ['Bank balance', snap.openingBalances.bank],
                    ['Loans receivable', snap.openingBalances.loansReceivable],
                    ['Vehicle inventory', snap.openingBalances.inventory],
                    ['Employee advances', snap.openingBalances.employeeAdvances],
                    ['Capital', snap.openingBalances.capital],
                    ['Profit brought forward', snap.openingBalances.profitBroughtForward],
                    ['Difference in opening', snap.openingBalances.openingDifference],
                  ].map(([label, value]) => (
                    <div key={label as string} className="rounded-lg bg-white/80 p-2 dark:bg-zinc-900/60">
                      <p className="text-zinc-500 dark:text-zinc-400">{label}</p>
                      <p className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{inr(value as number)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </ReportCard>
      )}

      {snap && tab === 'trial' && (
        <ReportCard
          title={t('erp.tab.trial')}
          subtitle={rangeLabel}
          exportRows={snap.trialBalance.rows.map((r) => ({
            Account: r.name,
            Debit: r.debit,
            Credit: r.credit,
            Balance: r.balance,
            Side: r.side,
          }))}
          exportTitle="Trial Balance"
          exportFilename="trial-balance"
        >
          <MobileTable
            headers={['Account', 'Debit', 'Credit', 'Balance', 'Side']}
            rows={snap.trialBalance.rows.map((r) => [r.name, inr(r.debit), inr(r.credit), inr(r.balance), r.side])}
          />
          <div
            className={`mt-4 rounded-lg border px-4 py-3 text-sm font-semibold ${
              snap.trialBalance.balanced
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-rose-200 bg-rose-50 text-rose-800'
            }`}
          >
            {snap.trialBalance.balanced
              ? 'Trial balance agrees'
              : `Trial balance is out by ${inr(snap.trialBalance.difference)} — ${t('erp.needsVerification')}`}
            <span className="ml-2 font-normal">
              Closing Dr {inr(snap.trialBalance.closingDebit ?? 0)} · Closing Cr {inr(snap.trialBalance.closingCredit ?? 0)}
            </span>
          </div>
        </ReportCard>
      )}

      {snap && tab === 'balance' && (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <ReportCard
              title={t('erp.liabilitiesEquity')}
              exportRows={[
                ...snap.balanceSheet.equity.map((a) => ({ Side: 'Capital', Item: a.name, Amount: a.amount })),
                ...snap.balanceSheet.liabilities.map((a) => ({ Side: 'Liabilities', Item: a.name, Amount: a.amount })),
                { Side: 'Total', Item: 'Total capital & liabilities', Amount: snap.balanceSheet.totalLiabilitiesAndEquity ?? snap.balanceSheet.totalLiabilities + snap.balanceSheet.totalEquity },
                ...snap.balanceSheet.assets.map((a) => ({ Side: 'Assets', Item: a.name, Amount: a.amount })),
                { Side: 'Total', Item: 'Total assets', Amount: snap.balanceSheet.totalAssets },
              ]}
              exportTitle="Balance Sheet"
              exportFilename="balance-sheet"
            >
              <MobileTable
                headers={['Item', 'Amount']}
                rows={[
                  ...snap.balanceSheet.equity.map((a) => [a.name, inr(a.amount)]),
                  ...snap.balanceSheet.liabilities.map((a) => [a.name, inr(a.amount)]),
                ]}
              />
              <p className="mt-3 text-sm font-bold">
                Total capital & liabilities: {inr(snap.balanceSheet.totalLiabilitiesAndEquity ?? snap.balanceSheet.totalLiabilities + snap.balanceSheet.totalEquity)}
              </p>
            </ReportCard>
            <ReportCard title={t('erp.assets')}>
              <MobileTable headers={['Item', 'Amount']} rows={snap.balanceSheet.assets.map((a) => [a.name, inr(a.amount)])} />
              <p className="mt-3 text-sm font-bold">Total assets: {inr(snap.balanceSheet.totalAssets)}</p>
            </ReportCard>
          </div>
          <div
            className={`rounded-lg border px-4 py-3 text-sm font-semibold ${
              snap.balanceSheet.balanced !== false
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-200'
                : 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-200'
            }`}
          >
            {snap.balanceSheet.balanced !== false
              ? `Balance sheet agrees — assets ${inr(snap.balanceSheet.totalAssets)} = capital & liabilities.`
              : `Balance sheet is out by ${inr(snap.balanceSheet.difference ?? 0)} — ${t('erp.needsVerification')}`}
            <span className="ml-2 font-normal">{t('erp.netWorth')}: {inr(snap.balanceSheet.netWorth)}</span>
          </div>
        </div>
      )}

      {snap && tab === 'pl' && (
        <ReportCard title={t('erp.tab.pl')} exportRows={[
          ...snap.profitAndLoss.income.map((i) => ({ Type: 'Income', Item: i.name, Amount: i.amount })),
          { Type: 'Income', Item: 'Total income', Amount: snap.profitAndLoss.totalIncome },
          ...snap.profitAndLoss.expenses.map((e) => ({ Type: 'Expense', Item: e.name, Amount: e.amount })),
          { Type: 'Expense', Item: 'Total expenses', Amount: snap.profitAndLoss.totalExpenses },
          { Type: 'Net', Item: snap.profitAndLoss.netProfit >= 0 ? 'Net Profit' : 'Net Loss', Amount: snap.profitAndLoss.netProfit },
        ]} exportTitle="Profit & Loss" exportFilename="profit-loss">
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase text-emerald-700">{t('erp.income')}</p>
              <MobileTable headers={['Item', 'Amount']} rows={snap.profitAndLoss.income.map((i) => [i.name, inr(i.amount)])} />
              <p className="mt-2 text-sm font-semibold">Total income: {inr(snap.profitAndLoss.totalIncome)}</p>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase text-rose-700">{t('erp.expenses')}</p>
              <MobileTable headers={['Item', 'Amount']} rows={snap.profitAndLoss.expenses.map((e) => [e.name, inr(e.amount)])} />
              <p className="mt-2 text-sm font-semibold">Total expenses: {inr(snap.profitAndLoss.totalExpenses)}</p>
            </div>
          </div>
          {snap.profitAndLoss.basis ? <p className="mt-3 text-xs text-zinc-500">{snap.profitAndLoss.basis}</p> : null}
          {snap.receivablesMemo && snap.receivablesMemo.overdueAmount > 0 ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
              <p className="font-semibold">
                Note: {inr(snap.receivablesMemo.overdueAmount)} of EMIs from {snap.receivablesMemo.overdueLoans}{' '}
                {snap.receivablesMemo.overdueLoans === 1 ? 'loan was' : 'loans were'} due by{' '}
                {new Date(`${snap.receivablesMemo.asOf}T00:00:00`).toLocaleDateString('en-IN')} but not received.
              </p>
              <p className="mt-0.5 text-xs">
                Of this, about {inr(snap.receivablesMemo.overdueInterest)} is interest. It is not counted in income above —
                interest is booked when the money is received.
              </p>
            </div>
          ) : null}
          <p className={`mt-4 text-lg font-bold ${snap.profitAndLoss.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
            {snap.profitAndLoss.netProfit >= 0
              ? `${t('erp.netProfit')}: ${inr(snap.profitAndLoss.netProfit)}`
              : `Net loss: ${inr(Math.abs(snap.profitAndLoss.netProfit))}`}
          </p>
        </ReportCard>
      )}

      {snap && tab === 'cashflow' && (
        <ReportCard title={t('erp.tab.cashflow')}>
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-800/50 dark:bg-amber-950/30">
              <p className="text-[10px] font-semibold uppercase text-amber-800 dark:text-amber-300">{t('payment.cash')}</p>
              <p className="text-lg font-bold text-amber-950 dark:text-amber-200">{inr(snap.cashFlow.closingCash ?? snap.cashFlow.openingCash)}</p>
              <p className="text-[11px] text-amber-800/80 dark:text-amber-300/80">{t('erp.closingCash')}</p>
            </div>
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 dark:border-sky-800/60 dark:bg-sky-950/35">
              <p className="text-[10px] font-semibold uppercase text-sky-800 dark:text-sky-300">{t('payment.online')}</p>
              <p className="text-lg font-bold text-sky-950 dark:text-sky-100">{inr(snap.cashFlow.closingBank ?? 0)}</p>
              <p className="text-[11px] text-sky-800/80 dark:text-sky-300/80">{t('erp.bankBook')}</p>
            </div>
          </div>
          {(() => {
            const cf = snap.cashFlow;
            const lines: { label: string; cash: number; bank: number; total?: boolean }[] = [
              { label: 'Opening balance', cash: cf.openingCash, bank: cf.openingBank ?? 0, total: true },
              { label: '+ EMI / loan collections', cash: cf.cashCollections ?? 0, bank: cf.bankCollections ?? 0 },
              { label: '+ Vehicle sales', cash: cf.cashSales ?? 0, bank: cf.bankSales ?? 0 },
              { label: '− Loans given', cash: cf.cashDisbursements ?? 0, bank: cf.bankDisbursements ?? 0 },
              { label: '− Expenses, salaries & advances', cash: cf.cashExpenses ?? 0, bank: cf.bankExpenses ?? 0 },
              { label: '− Vehicle purchases', cash: cf.cashPurchases ?? 0, bank: cf.bankPurchases ?? 0 },
            ];
            if (cf.cashOther || cf.bankOther) lines.push({ label: '± Other journal entries', cash: cf.cashOther ?? 0, bank: cf.bankOther ?? 0 });
            lines.push({ label: 'Closing balance', cash: cf.closingCash, bank: cf.closingBank ?? 0, total: true });
            return (
              <MobileTable
                headers={['Particulars', 'Cash', 'Bank', 'Total']}
                rows={lines.map((l) => [l.total ? `▸ ${l.label}` : l.label, inr(l.cash), inr(l.bank), inr(l.cash + l.bank)])}
              />
            );
          })()}
        </ReportCard>
      )}

      {snap && tab === 'daybook' && (
        <div className="space-y-3">
          {snap.dayBook.map((day) => (
            <ReportCard
              key={day.date}
              title={day.label}
              subtitle={`${day.items.length} entries · in ${inr(day.moneyIn ?? 0)} · out ${inr(day.moneyOut ?? 0)}`}
            >
              <MobileTable
                headers={['Description', 'Money in', 'Money out']}
                rows={day.items.map((it) => [
                  it.description,
                  it.direction === 'out' ? '—' : inr(it.amount),
                  it.direction === 'out' ? inr(it.amount) : '—',
                ])}
              />
            </ReportCard>
          ))}
          {snap.dayBook.length === 0 ? <p className="text-sm text-zinc-500">{t('erp.noData')}</p> : null}
        </div>
      )}

      {snap && tab === 'ledger' && (
        <div className="space-y-4">
          <select
            value={selectedLedger}
            onChange={(e) => setSelectedLedger(e.target.value)}
            className="rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          >
            <option value="all">{t('common.all')} ledgers</option>
            {snap.ledgerBooks.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
          {filteredLedgers.map((ledger) => (
            <ReportCard key={ledger.code} title={ledger.name} subtitle={`Balance: ${inr(ledger.balance)}`}>
              <MobileTable
                headers={['Date', 'Description', 'Debit', 'Credit', 'Balance']}
                rows={ledger.entries.map((e) => [
                  e.date ? new Date(e.date).toLocaleDateString('en-IN') : '—',
                  e.description,
                  e.debit ? inr(e.debit) : '—',
                  e.credit ? inr(e.credit) : '—',
                  inr(e.balance),
                ])}
              />
            </ReportCard>
          ))}
        </div>
      )}

      {snap && tab === 'journal' && (
        <div className="space-y-3">
          {snap.journalEntries.map((entry) => (
            <div key={entry.clientId} className="app-card p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{entry.description}</p>
                  <p className="text-xs text-zinc-500">
                    {new Date(entry.entryDate).toLocaleString('en-IN')} · {entry.sourceType || 'journal'}
                  </p>
                </div>
                {canWrite &&
                !entry.reversalEntryId &&
                !entry.isReversal &&
                !String(entry.clientId || '').startsWith('auto_') ? (
                  <button
                    type="button"
                    onClick={() => handleReverse(entry.clientId)}
                    className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {t('erp.reverse')}
                  </button>
                ) : null}
              </div>
              <div className="mt-3">
                <MobileTable
                  headers={['Account', 'Debit', 'Credit']}
                  rows={(entry.lines || []).map((l) => [l.accountName || l.account, l.debit ? inr(l.debit) : '—', l.credit ? inr(l.credit) : '—'])}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {snap && tab === 'reconcile' && (
        <ReportCard title={t('erp.tab.reconcile')} subtitle={t('erp.reconcileHint')}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-zinc-200 p-4">
              <p className="text-xs font-semibold uppercase text-zinc-500">{t('erp.cashBook')}</p>
              <p className="mt-1 text-2xl font-bold">{inr(snap.reconciliation.cashBook)}</p>
            </div>
            <div className="rounded-xl border border-zinc-200 p-4">
              <p className="text-xs font-semibold uppercase text-zinc-500">{t('erp.bankBook')}</p>
              <p className="mt-1 text-2xl font-bold">{inr(snap.reconciliation.bankBook)}</p>
            </div>
            <div className="rounded-xl border border-zinc-200 p-4">
              <p className="text-xs font-semibold uppercase text-zinc-500">{t('erp.booksTotal')}</p>
              <p className="mt-1 text-2xl font-bold">{inr(snap.reconciliation.booksTotal ?? (snap.reconciliation.cashBook + snap.reconciliation.bankBook))}</p>
            </div>
            <div
              className={`rounded-xl border p-4 ${
                snap.reconciliation.needsVerification
                  ? 'border-rose-200 bg-rose-50'
                  : 'border-emerald-200 bg-emerald-50'
              }`}
            >
              <p className="text-xs font-semibold uppercase text-zinc-500">{t('erp.difference')}</p>
              <p className={`mt-1 text-2xl font-bold ${snap.reconciliation.needsVerification ? 'text-rose-700' : 'text-emerald-700'}`}>
                {inr(snap.reconciliation.difference)}
              </p>
              <p className="mt-1 text-xs text-zinc-600">
                {t('erp.flowTotal')}: {inr(snap.reconciliation.flowTotal ?? snap.cashFlow?.closingTotal ?? 0)}
              </p>
              {snap.reconciliation.needsVerification ? (
                <p className="mt-1 text-xs font-semibold text-rose-700">{t('erp.needsVerification')}</p>
              ) : (
                <p className="mt-1 text-xs font-semibold text-emerald-700">{t('erp.matched')}</p>
              )}
            </div>
          </div>
        </ReportCard>
      )}

      {snap && tab === 'auditreport' && (
        <div className="space-y-4">
          <ReportCard
            title="Financial year report for CA / accountant"
            subtitle={`${snap.financialYear?.label || ''} ${formatRangeLabel({
              start: snap.period?.from ? new Date(`${snap.period.from}T00:00:00`) : null,
              end: snap.period?.to ? new Date(`${snap.period.to}T00:00:00`) : null,
            })}`.trim()}
          >
            <p className="mb-3 text-sm text-zinc-600 dark:text-zinc-300">
              One file with the summary, Profit &amp; Loss account, Balance Sheet, Trial Balance, Cash &amp; Bank summary and the
              checks below — for the period selected above. Pick “Financial year” in the date filter for a full year.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={downloadFyPdf}
                disabled={fyExporting || loading}
                className="app-btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-60"
              >
                <FileText className="h-4 w-4" />
                {fyExporting ? 'Preparing PDF…' : 'Download PDF'}
              </button>
              <button
                type="button"
                onClick={() => exportFyReportExcel(snap)}
                disabled={loading}
                className="app-btn-secondary inline-flex items-center gap-1.5 text-sm disabled:opacity-60"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Download Excel
              </button>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase text-zinc-500">Books checks</p>
              <CheckList checks={snap.checks || []} />
            </div>
          </ReportCard>
          <ReportCard
            title={t('erp.tab.auditreport')}
            subtitle={t('erp.auditReportSubtitle')}
            exportRows={auditReportRows}
            exportTitle="Audit Report Package"
            exportFilename="audit-report"
          >
            <p className="text-sm text-zinc-600 dark:text-zinc-300">{t('erp.auditReportHint')}</p>
            <button type="button" onClick={() => setActiveItem('audit')} className="app-btn-secondary mt-4 text-sm">
              {t('erp.viewAuditLogs')}
            </button>
          </ReportCard>
        </div>
      )}
    </div>
  );
}

function num(v: unknown) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
